import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateTopicRead } from 'src/queries/chat/api';
import { chatKeys } from 'src/queries/chat/keys';
import type { ChatMessage, MessageCenterRow } from 'src/models/chat';

/** MSG-04: a read acknowledges only the loaded topic, never its event conversation. */
export function useTopicRead(chatId: number, key: string | null, readState?: MessageCenterRow['readState']) {
  const cache = useQueryClient(), acked = useRef(''), held = useRef('');
  const identity = `${chatId}:${key || ''}`;
  const [retry, setRetry] = useState(0);
  const mutation = useMutation((body: { messageId?: number; unread?: boolean }) => updateTopicRead(chatId, { key: key!, ...body, expectedUpdatedAt: readState?.updatedAt ?? null }), {
    onSettled: () => { cache.invalidateQueries(chatKeys.messageCenter()); cache.invalidateQueries(chatKeys.chatTopics(chatId)); }
  });
  const current = useRef(mutation); current.current = mutation;
  const onReadMessages = useCallback((messages: ChatMessage[]) => {
    if (!key || !messages.length || current.current.isLoading || held.current === identity) return;
    const id = messages[messages.length - 1].id, signature = `${identity}:${id}`;
    if (acked.current === signature) return;
    acked.current = signature;
    current.current.mutate({ messageId: id });
  }, [identity, key, retry, readState?.updatedAt, mutation.isLoading]);
  return { onReadMessages, pending: mutation.isLoading, error: mutation.isError,
    markUnread: () => { held.current = identity; mutation.mutate({ unread: true }); },
    retry: () => { if (held.current === identity) mutation.mutate({ unread: true }); else { acked.current = ''; mutation.reset(); setRetry(value => value + 1); } } };
}
